import * as React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { FullPageLoader } from "@/components/common/full-page-loader";
import { RequireAnonymous, RequireAuth, RequireStaff } from "@/components/routing/guards";
import { ScrollToTop } from "@/components/routing/scroll-to-top";
import { lazyWithReload } from "@/lib/chunk-reload";

/* Auth screens are the first paint for a signed-out visitor, so they stay eager;
   everything behind the shell is split. */
import LoginPage from "@/routes/auth/login";
import SignupPage from "@/routes/auth/signup";
import ForgotPasswordPage from "@/routes/auth/forgot-password";
import ResetPasswordPage from "@/routes/auth/reset-password";
import VerifyEmailPage from "@/routes/auth/verify-email";

/** The shell carries the nav, command palette and banners — signed-out
    visitors never need any of it, so it is split out too. */
const AppShell = lazyWithReload(() =>
  import("@/components/layout/app-shell").then((m) => ({ default: m.AppShell })),
);

const DashboardPage = lazyWithReload(() => import("@/routes/app/dashboard"));
const FiguresPage = lazyWithReload(() => import("@/routes/app/figures"));
const FigurePeriodPage = lazyWithReload(() => import("@/routes/app/figure-period"));
const ValuationPage = lazyWithReload(() => import("@/routes/app/valuation"));
const TasksPage = lazyWithReload(() => import("@/routes/app/tasks"));
const RaiPage = lazyWithReload(() => import("@/routes/app/rai"));
const PortfolioPage = lazyWithReload(() => import("@/routes/app/portfolio"));
const DocumentsPage = lazyWithReload(() => import("@/routes/app/documents"));
const AcademyPage = lazyWithReload(() => import("@/routes/app/academy"));
const AcademyItemPage = lazyWithReload(() => import("@/routes/app/academy-item"));
const ProgressPage = lazyWithReload(() => import("@/routes/app/progress"));
const BillingPage = lazyWithReload(() => import("@/routes/app/billing"));

const SettingsLayout = lazyWithReload(() => import("@/routes/settings/settings-layout"));
const ProfileSettings = lazyWithReload(() => import("@/routes/settings/profile"));
const SecuritySettings = lazyWithReload(() => import("@/routes/settings/security"));
const BusinessSettings = lazyWithReload(() => import("@/routes/settings/business"));
const MembersSettings = lazyWithReload(() => import("@/routes/settings/members"));
const AppearanceSettings = lazyWithReload(() => import("@/routes/settings/appearance"));
const NotificationSettings = lazyWithReload(() => import("@/routes/settings/notifications"));
const NewBusinessPage = lazyWithReload(() => import("@/routes/settings/new-business"));

const AdminLayout = lazyWithReload(() => import("@/routes/admin/admin-layout"));
const AdminOverview = lazyWithReload(() => import("@/routes/admin/overview"));
const AdminBusinesses = lazyWithReload(() => import("@/routes/admin/businesses"));
const AdminBusinessDetail = lazyWithReload(() => import("@/routes/admin/business-detail"));
const AdminTasks = lazyWithReload(() => import("@/routes/admin/tasks"));
const AdminInvites = lazyWithReload(() => import("@/routes/admin/invites"));
const AdminStaff = lazyWithReload(() => import("@/routes/admin/staff"));
const AdminScoring = lazyWithReload(() => import("@/routes/admin/scoring"));
const AdminCron = lazyWithReload(() => import("@/routes/admin/cron"));

const NotFoundPage = lazyWithReload(() => import("@/routes/not-found"));

export default function App() {
  return (
    <>
      <ScrollToTop />
      <React.Suspense fallback={<FullPageLoader />}>
        <Routes>
          {/* Public */}
          <Route
            path="/login"
            element={
              <RequireAnonymous>
                <LoginPage />
              </RequireAnonymous>
            }
          />
          <Route
            path="/signup"
            element={
              <RequireAnonymous>
                <SignupPage />
              </RequireAnonymous>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <RequireAnonymous>
                <ForgotPasswordPage />
              </RequireAnonymous>
            }
          />
          <Route
            path="/reset-password"
            element={
              <RequireAnonymous>
                <ResetPasswordPage />
              </RequireAnonymous>
            }
          />
          {/* Reachable either way: the link may be opened in a fresh browser */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Authenticated app */}
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/figures" element={<FiguresPage />} />
            <Route path="/figures/:period" element={<FigurePeriodPage />} />
            <Route path="/valuation" element={<ValuationPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/rai" element={<RaiPage />} />
            <Route path="/rai/:conversationId" element={<RaiPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/academy" element={<AcademyPage />} />
            <Route path="/academy/:slug" element={<AcademyItemPage />} />
            <Route path="/progress" element={<ProgressPage />} />
            <Route path="/billing" element={<BillingPage />} />

            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<ProfileSettings />} />
              <Route path="profile" element={<Navigate to="/settings" replace />} />
              <Route path="security" element={<SecuritySettings />} />
              <Route path="business" element={<BusinessSettings />} />
              <Route path="members" element={<MembersSettings />} />
              <Route path="appearance" element={<AppearanceSettings />} />
              <Route path="notifications" element={<NotificationSettings />} />
            </Route>
            <Route path="/settings/businesses/new" element={<NewBusinessPage />} />
          </Route>

          {/* Staff console */}
          <Route
            element={
              <RequireStaff>
                <AppShell />
              </RequireStaff>
            }
          >
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminOverview />} />
              <Route path="businesses" element={<AdminBusinesses />} />
              <Route path="businesses/:id" element={<AdminBusinessDetail />} />
              <Route path="tasks" element={<AdminTasks />} />
              <Route path="invites" element={<AdminInvites />} />
              <Route path="staff" element={<AdminStaff />} />
              <Route path="scoring" element={<AdminScoring />} />
              <Route path="cron" element={<AdminCron />} />
            </Route>
          </Route>

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </React.Suspense>
    </>
  );
}
